import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/use-auth';
import { Loader } from '@tasama/ui';

interface ProtectedRouteProps {
  children: React.ReactNode;
  testID?: string | undefined;
}

/**
 * ProtectedRoute — redirects to login if the user is not authenticated.
 *
 * With cookie-based auth there is no JS-readable token to inspect.
 * Auth state is determined entirely by the server via AuthProvider on app load.
 * Here we just react to the in-memory isAuthenticated flag.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  testID = 'protected-route',
}) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        data-testid={`${testID}-loading`}
      >
        <Loader size="lg" testID={`${testID}-loader`} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
        data-testid={`${testID}-redirect`}
      />
    );
  }

  return <div data-testid={testID}>{children}</div>;
};

export default ProtectedRoute;
